import type { LoginResponse } from '@taskos/shared';
import { pinProblem } from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { errorMessage, post, setCsrf } from '../api/client';
import { PinInput } from '../components/PinInput';
import { Button, Field } from '../components/ui';

type Step =
  | { kind: 'signin' }
  | { kind: '2fa'; challengeId: string; method: 'TOTP' | 'TELEGRAM'; delivered: boolean }
  | { kind: 'forgot' }
  | { kind: 'reset'; challengeId: string; userId: string }
  | { kind: 'reset-done' };

export function LoginPage() {
  const [step, setStep] = useState<Step>({ kind: 'signin' });
  const [userId, setUserId] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [newPin2, setNewPin2] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const next = params.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const finish = async (csrf: string) => {
    setCsrf(csrf);
    await qc.resetQueries();
    nav(safeNext, { replace: true });
  };

  const signIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim() || !pin) {
      setError('Enter your User ID and PIN.');
      return;
    }
    void run(async () => {
      try {
        const r = await post<LoginResponse>('/api/auth/login', { userId: userId.trim(), pin });
        if (r.ok) await finish(r.csrfToken);
        else {
          setCode('');
          setUseRecovery(false);
          setStep({ kind: '2fa', ...r.twoFactor });
        }
      } finally {
        setPin('');
      }
    });
  };

  const verify2fa = (e: React.FormEvent) => {
    e.preventDefault();
    if (step.kind !== '2fa') return;
    void run(async () => {
      const r = await post<{ ok: true; csrfToken: string }>('/api/auth/login/2fa', { challengeId: step.challengeId, code: code.trim() });
      await finish(r.csrfToken);
    });
  };

  const requestReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) {
      setError('Enter your User ID.');
      return;
    }
    void run(async () => {
      const r = await post<{ challengeId: string; message: string }>('/api/auth/forgot-pin', { userId: userId.trim() });
      setInfo(r.message);
      setCode('');
      setStep({ kind: 'reset', challengeId: r.challengeId, userId: userId.trim() });
    });
  };

  const newPinProblem = newPin ? pinProblem(newPin) : null;
  const doReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'reset') return;
    const p = pinProblem(newPin);
    if (p) return setError(p);
    if (newPin !== newPin2) return setError('The two PINs do not match');
    void run(async () => {
      await post('/api/auth/reset-pin', { challengeId: step.challengeId, code: code.trim(), newPin });
      setNewPin('');
      setNewPin2('');
      setInfo(null);
      setStep({ kind: 'reset-done' });
    });
  };

  const back = () => {
    setError(null);
    setInfo(null);
    setStep({ kind: 'signin' });
  };

  return (
    <div className="auth-wrap">
      <main className="auth-card card card-pad stack">
        <div className="flex center gap">
          <img src="/favicon.svg" width={36} height={36} alt="" />
          <div>
            <h1>Task OS</h1>
            <p className="muted small">
              {step.kind === 'signin' ? 'Sign in with your User ID and PIN' : step.kind === '2fa' ? 'Two-step verification' : step.kind === 'reset-done' ? 'PIN changed' : 'Reset your PIN'}
            </p>
          </div>
        </div>

        {error && (
          <div className="inline-alert danger" role="alert">
            {error}
          </div>
        )}
        {info && (
          <div className="inline-alert info" role="status">
            {info}
          </div>
        )}

        {step.kind === 'signin' && (
          <form className="stack" onSubmit={signIn} noValidate>
            <Field label="User ID" htmlFor="userId">
              <input id="userId" className="input" autoComplete="username" autoCapitalize="none" spellCheck={false} value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus />
            </Field>
            <Field label="PIN" htmlFor="pin">
              <PinInput id="pin" value={pin} onChange={setPin} autoComplete="current-password" />
            </Field>
            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            <button
              type="button"
              className="link-btn small"
              style={{ alignSelf: 'center' }}
              onClick={() => {
                setError(null);
                setStep({ kind: 'forgot' });
              }}
            >
              Forgot PIN?
            </button>
          </form>
        )}

        {step.kind === '2fa' && (
          <form className="stack" onSubmit={verify2fa} noValidate>
            <div className="inline-row small muted">
              <ShieldCheck size={18} aria-hidden />
              {useRecovery
                ? 'Enter one of your recovery codes (each works once).'
                : step.method === 'TOTP'
                  ? 'Open your authenticator app and enter the 6-digit code for Task OS.'
                  : step.delivered
                    ? 'We sent a 6-digit code to your Telegram (Task bot chat).'
                    : "We couldn't reach Telegram right now. Use a recovery code, or try again shortly."}
            </div>
            <Field label={useRecovery ? 'Recovery code' : 'Code'} htmlFor="code">
              <input
                id="code"
                className="input otp-input"
                inputMode={useRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                maxLength={useRecovery ? 9 : 6}
                placeholder={useRecovery ? 'XXXX-XXXX' : '123456'}
                value={code}
                onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            </Field>
            <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={useRecovery ? code.replace('-', '').length < 8 : code.length !== 6}>
              Verify
            </Button>
            <div className="flex between">
              <button type="button" className="link-btn small" onClick={back}>
                <ArrowLeft size={14} aria-hidden /> Back
              </button>
              <button
                type="button"
                className="link-btn small"
                onClick={() => {
                  setUseRecovery((r) => !r);
                  setCode('');
                }}
              >
                {useRecovery ? 'Use the 6-digit code' : 'Use a recovery code'}
              </button>
            </div>
          </form>
        )}

        {step.kind === 'forgot' && (
          <form className="stack" onSubmit={requestReset} noValidate>
            <p className="small muted">
              <KeyRound size={14} aria-hidden /> Enter your User ID. If your account has the Task bot linked on Telegram, we'll send a reset code there.
            </p>
            <Field label="User ID" htmlFor="fp-user">
              <input id="fp-user" className="input" autoCapitalize="none" spellCheck={false} value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus />
            </Field>
            <Button type="submit" variant="primary" block loading={busy}>
              Send code via Telegram
            </Button>
            <p className="hint">No Telegram linked? Ask your admin to reset your PIN.</p>
            <button type="button" className="link-btn small" style={{ alignSelf: 'flex-start' }} onClick={back}>
              <ArrowLeft size={14} aria-hidden /> Back to sign in
            </button>
          </form>
        )}

        {step.kind === 'reset' && (
          <form className="stack" onSubmit={doReset} noValidate>
            <Field label="6-digit code from Telegram" htmlFor="rs-code">
              <input id="rs-code" className="input otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
            </Field>
            <Field label="New PIN" htmlFor="rs-pin" error={newPinProblem} hint="At least 4 digits, not all the same">
              <PinInput id="rs-pin" value={newPin} onChange={setNewPin} autoComplete="new-password" invalid={!!newPinProblem} />
            </Field>
            <Field label="Repeat new PIN" htmlFor="rs-pin2" error={newPin2 && newPin2 !== newPin ? 'PINs do not match' : null}>
              <PinInput id="rs-pin2" value={newPin2} onChange={setNewPin2} autoComplete="new-password" />
            </Field>
            <Button type="submit" variant="primary" block loading={busy} disabled={code.length !== 6 || !newPin || !!newPinProblem || newPin !== newPin2}>
              Set new PIN
            </Button>
            <button type="button" className="link-btn small" style={{ alignSelf: 'flex-start' }} onClick={back}>
              <ArrowLeft size={14} aria-hidden /> Back to sign in
            </button>
          </form>
        )}

        {step.kind === 'reset-done' && (
          <div className="stack">
            <div className="inline-alert success">Your PIN has been changed and all devices were signed out. Sign in with your new PIN.</div>
            <Button variant="primary" block onClick={back}>
              Sign in
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
